import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlayerFinance = () => {
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingInvoicePdf, setDownloadingInvoicePdf] = useState(null);

  useEffect(() => {
    fetchFinanceData();
  }, []);

  const fetchFinanceData = async () => {
    try {
      const athletesRes = await axios.get(`${API}/athletes`);
      if (athletesRes.data.length > 0) {
        const athleteData = athletesRes.data[0];
        setAthlete(athleteData);
        
        const [invoicesRes, summaryRes] = await Promise.all([
          axios.get(`${API}/invoices/athlete/${athleteData.id}`),
          axios.get(`${API}/finance/athlete/${athleteData.id}/summary`)
        ]);
        
        setInvoices(invoicesRes.data);
        setSummary(summaryRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch finance data');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
    setDownloadingInvoicePdf(invoiceId);
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Invoice PDF downloaded');
    } catch (error) {
      toast.error('Failed to download invoice PDF');
    } finally {
      setDownloadingInvoicePdf(null);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      overdue: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || colors.pending;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Finance & Payments
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1">View your invoices and payment history</p>
          </div>
          <Button
            onClick={() => navigate('/my-finance/reports')}
            variant="outline"
            className="gap-2"
            data-testid="view-reports"
          >
            📊 View Reports
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : !athlete ? (
          <Card data-testid="no-athlete-message">
            <CardContent className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-500">Profile not linked. Contact administrator.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Financial Summary Card */}
            {summary && (
              <Card data-testid="finance-summary-card">
                <CardHeader>
                  <CardTitle>Financial Summary</CardTitle>
                  <CardDescription>Your account overview</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-blue-700 font-medium">Total Invoiced</p>
                      <p className="text-2xl font-bold text-blue-900" data-testid="total-invoiced">
                        {formatCurrency(summary.total_invoiced)}
                      </p>
                    </div>
                    <div className="bg-emerald-50 p-4 rounded-lg">
                      <p className="text-sm text-emerald-700 font-medium">Total Paid</p>
                      <p className="text-2xl font-bold text-emerald-900" data-testid="total-paid">
                        {formatCurrency(summary.total_paid)}
                      </p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <p className="text-sm text-amber-700 font-medium">Pending</p>
                      <p className="text-2xl font-bold text-amber-900" data-testid="total-pending">
                        {formatCurrency(summary.total_pending)}
                      </p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="text-sm text-red-700 font-medium">Outstanding Balance</p>
                      <p className="text-2xl font-bold text-red-900" data-testid="outstanding-balance">
                        {formatCurrency(summary.outstanding_balance)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Invoices List */}
            <Card>
              <CardHeader>
                <CardTitle>Invoices & Payments</CardTitle>
                <CardDescription>Your billing history</CardDescription>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-500 text-center py-8">No invoices available</p>
                ) : (
                  <div className="space-y-4" data-testid="invoices-list">
                    {invoices.map((invoice) => (
                      <Card key={invoice.id} className="border-2" data-testid={`invoice-${invoice.id}`}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <CardTitle className="text-lg">{invoice.invoice_number}</CardTitle>
                                <Badge className={getStatusBadge(invoice.status)} data-testid={`invoice-status-${invoice.id}`}>
                                  {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                </Badge>
                              </div>
                              <CardDescription>{invoice.description}</CardDescription>
                            </div>
                            <div className="text-right ml-4">
                              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-testid={`invoice-amount-${invoice.id}`}>
                                {formatCurrency(invoice.amount)}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                                Due: {new Date(invoice.due_date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 dark:text-gray-500">
                              Created: {new Date(invoice.created_at).toLocaleDateString()}
                            </span>
                            <div className="flex items-center gap-2">
                              {invoice.status === 'overdue' && (
                                <div className="bg-red-50 text-red-700 px-3 py-1 rounded-full text-xs font-medium">
                                  Payment Overdue
                                </div>
                              )}
                              {invoice.status === 'pending' && new Date(invoice.due_date) < new Date() && (
                                <div className="bg-red-50 text-red-700 px-3 py-1 rounded-full text-xs font-medium">
                                  Payment Due
                                </div>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadInvoicePdf(invoice.id, invoice.invoice_number)}
                                disabled={downloadingInvoicePdf === invoice.id}
                                data-testid={`download-invoice-pdf-${invoice.id}`}
                              >
                                {downloadingInvoicePdf === invoice.id ? '...' : '📄 PDF'}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Instructions */}
            {summary && summary.outstanding_balance > 0 && (
              <Card className="bg-amber-50 border-amber-200">
                <CardHeader>
                  <CardTitle className="text-amber-900">Outstanding Balance</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-amber-800 mb-4">
                    You have an outstanding balance of <strong>{formatCurrency(summary.outstanding_balance)}</strong>.
                  </p>
                  <p className="text-sm text-amber-700">
                    Please contact the academy office for payment arrangements or to discuss your account.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlayerFinance;
